/**
 * Plain-words presentation for the Shopify store connection card: status
 * badges (DESIGN.md §13.1 / §13.10 palette: emerald connected, amber waiting
 * or needs attention, muted grey for history), failure reasons and save
 * outcomes. Operators never see a raw reason code or a raw OAuth scope.
 */
import type { TFunction } from 'i18next';
import type { StatusStyle } from '@/components/ui/status-badge';
import type { RouterOutputs } from '@/lib/trpc';

export type OrgConnection = RouterOutputs['shopifyIntegration']['getConnectionStatus'];
export type PlatformConnection = RouterOutputs['platform']['getShopifyConnection'];
/** The read-model the section renders: the platform one, whose extras are absent in org scope. */
export type ConnectionView = OrgConnection & {
  customApp: (NonNullable<OrgConnection['customApp']> & { clientId?: string; registeredAt?: string | null }) | null;
  setup?: PlatformConnection['setup'];
};
export type CustomAppView = NonNullable<ConnectionView['customApp']>;
export type CustomAppStatus = CustomAppView['status'];
export type ConnectionMode = ConnectionView['mode'];

export type SaveOutcome = RouterOutputs['shopifyIntegration']['saveConnection'];
export type PlatformSaveOutcome = RouterOutputs['platform']['saveShopifyConnection'];

const TONE = {
  emerald: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300/80',
  amber: 'bg-amber-100 text-amber-800 ring-1 ring-amber-300/80',
  muted: 'bg-muted text-muted-foreground ring-1 ring-border',
} as const;

export function customAppStatusStyle(t: TFunction, status: CustomAppStatus): StatusStyle {
  switch (status) {
    case 'connected':
      return { label: t('shopifyConnection.store.status.connected'), className: TONE.emerald };
    case 'exchanging':
      return { label: t('shopifyConnection.store.status.exchanging'), className: TONE.amber };
    case 'failed':
      return { label: t('shopifyConnection.store.status.failed'), className: TONE.amber };
    case 'uninstalled':
      return { label: t('shopifyConnection.store.status.uninstalled'), className: TONE.muted };
    default:
      return { label: t('shopifyConnection.store.status.waiting'), className: TONE.amber };
  }
}

export function modeStatusStyle(t: TFunction, mode: ConnectionMode): StatusStyle {
  return mode === 'none'
    ? { label: t('shopifyConnection.store.status.notConnected'), className: TONE.muted }
    : { label: t('shopifyConnection.store.status.connected'), className: TONE.emerald };
}

/** Own-property lookup: a stored reason such as `toString` must not hit Object.prototype. */
function ownKey(map: Record<string, string>, code: string | null): string | null {
  return code !== null && Object.prototype.hasOwnProperty.call(map, code) ? (map[code] ?? null) : null;
}

/**
 * Slice-1 failure reason codes (`shopify-custom-app-connect.ts`), grouped into
 * the sentences an operator can act on. Anything unlisted (`*_failed`,
 * `*_invalid_response`, a future code) gets the generic sentence.
 */
const FAILURE_UNREACHABLE = 'shopifyConnection.store.failure.unreachable';
const FAILURE_REFUSED = 'shopifyConnection.store.failure.refused';
const FAILURE_STORAGE = 'shopifyConnection.store.failure.storage';
const FAILURE_REASON_KEYS: Record<string, string> = {
  hmac_mismatch: 'shopifyConnection.store.failure.secret',
  exchange_http_status: FAILURE_REFUSED,
  exchange_invalid_input: FAILURE_REFUSED,
  exchange_network: FAILURE_UNREACHABLE,
  exchange_timeout: FAILURE_UNREACHABLE,
  identity_network: FAILURE_UNREACHABLE,
  identity_timeout: FAILURE_UNREACHABLE,
  exchange_expiring_token: 'shopifyConnection.store.failure.expiringToken',
  identity_mismatch: 'shopifyConnection.store.failure.identityMismatch',
  domain_taken: 'shopifyConnection.store.failure.domainTaken',
  domain_mismatch: 'shopifyConnection.store.failure.domainMismatch',
  vault_write_failed: FAILURE_STORAGE,
  vault_cleanup_failed: FAILURE_STORAGE,
  // The exchange died mid-flight (its 60s lease ran out); the server settles it.
  interrupted: 'shopifyConnection.store.failure.interrupted',
};

export function failureReasonText(t: TFunction, reason: string | null): string {
  return t(ownKey(FAILURE_REASON_KEYS, reason) ?? 'shopifyConnection.store.failure.generic');
}

/** Plain words for a Shopify scope (`read_orders` → "read orders"). Reuses the junction panel's phrases. */
export function scopePhrase(t: TFunction, scope: string): string {
  const separator = scope.indexOf('_');
  const verb = separator > 0 ? scope.slice(0, separator) : '';
  const subject = (separator > 0 ? scope.slice(separator + 1) : scope).replace(/_/g, ' ').trim();
  if (subject.length === 0) return scope.replace(/_/g, ' ');
  if (verb === 'read') return t('shopifyConnection.scopes.read', { subject });
  if (verb === 'write') return t('shopifyConnection.scopes.write', { subject });
  return t('shopifyConnection.scopes.plain', { subject: scope.replace(/_/g, ' ') });
}

/** Error sentences for a save outcome, keyed by the backend's reason code. */
const SAVE_ERROR_KEYS: Record<string, string> = {
  credentials_rejected: 'shopifyConnection.store.saveError.credentials_rejected',
  app_not_installed: 'shopifyConnection.store.saveError.app_not_installed',
  shopify_unreachable: 'shopifyConnection.store.saveError.shopify_unreachable',
  exchange_failed: 'shopifyConnection.store.saveError.exchange_failed',
  invalid_client_id: 'shopifyConnection.store.saveError.invalid_client_id',
  invalid_shop: 'shopifyConnection.store.saveError.invalid_shop',
  missing_secret: 'shopifyConnection.store.saveError.missing_secret',
  org_not_found: 'shopifyConnection.store.saveError.org_not_found',
  domain_mismatch: 'shopifyConnection.store.saveError.domain_mismatch',
  app_or_store_taken: 'shopifyConnection.store.saveError.app_or_store_taken',
  vault_error: 'shopifyConnection.store.saveError.vault_error',
};

export function saveErrorText(t: TFunction, reason: string): string {
  return t(ownKey(SAVE_ERROR_KEYS, reason) ?? 'shopifyConnection.store.saveError.generic');
}

/** Same rule as the backend (and the operator script): https on admin.shopify.com. */
export function isShopifyInstallLink(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' && url.hostname === 'admin.shopify.com';
  } catch {
    return false;
  }
}

/** `{store}.myshopify.com`, as the backend's shared domain schema expects. */
export function normalizeShopDomain(value: string): string {
  return value.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
}
